import React, { useMemo } from "react";
import { Container, Icon, Loader, Message } from "semantic-ui-react";

import { useGetCustomerPersonasInfiniteScrollQuery } from "../customer_personas/customerPersonaApiSlice";
import { CustomerPersonaCard, AI_IMAGE_NOTICE } from "../customer_personas/CustomerPersonaCards";

/**
 * 사전 연습 persona choice.
 *
 * The client asked for two customers here rather than the single random one this flow
 * used to start with. Two are drawn from the pool per visit rather than being fixed, so
 * repeated practice does not always face the same pair.
 *
 * Reuses CustomerPersonaCard, which already renders the scene, name, location and the
 * 시작하기 button beneath the image — the card creates a practice session because
 * isPractice is passed, so the mode can never disagree with the screen.
 */

const PRACTICE_PERSONA_COUNT = 2;

const PracticePersonaSelect = () => {
  const { data, isFetching } = useGetCustomerPersonasInfiniteScrollQuery(1);
  const personas = useMemo(() => data?.results ?? [], [data]);

  // Keyed on the ids so the pair stays put while the page is open, but is redrawn on a
  // fresh visit.
  const chosen = useMemo(() => {
    if (personas.length <= PRACTICE_PERSONA_COUNT) return personas;
    const pool = [...personas];
    const picked = [];
    while (picked.length < PRACTICE_PERSONA_COUNT && pool.length) {
      picked.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
    }
    return picked;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personas.map((p) => p.id).join(",")]);

  if (isFetching && personas.length === 0) {
    return <Loader active />;
  }

  return (
    <div style={{ backgroundColor: "#f9fafb", minHeight: "calc(100vh - 50px)" }}>
      <div
        style={{
          padding: "16px 0",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          marginBottom: "20px",
        }}
      >
        {/* Semantic's Container, matching 롤플레이 — this screen and that one are the
            same step of two different flows and were starting their titles 200px apart. */}
        <Container>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#F2F2F7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="clipboard outline" style={{ margin: 0, color: "#34C759", fontSize: "18px" }} />
            </div>
            <div>
              <h2 style={{ fontSize: "17px", fontWeight: "600", margin: 0, color: "#1d1d1f" }}>
                사전 연습
              </h2>
              <p style={{ fontSize: "13px", color: "#86868b", margin: 0 }}>
                아래 고객의 사진과 상황 설명을 확인하시고, 원하는 고객을 선택해 사전 연습을 시작하세요. 평가되지 않습니다.
              </p>
              <p style={{ fontSize: "12px", color: "#86868b", margin: "4px 0 0", display: "flex", alignItems: "center", gap: "5px" }}>
                <Icon name="info circle" style={{ margin: 0, fontSize: "12px", lineHeight: 1 }} />
                {AI_IMAGE_NOTICE}
              </p>
            </div>
          </div>
        </Container>
      </div>

      {/* Container here too, so the cards line up under the header rather than running
          200px wider than it. Note the absent inline `width: "100%"` — that, not Container
          itself, is what used to overflow: `.ui.container`'s mobile `margin: 0 1em`
          combines with a 100% width to push the content 2em off the right edge. */}
      <Container>
        {chosen.length === 0 && (
          <Message
            warning
            content="사용할 수 있는 고객 페르소나가 없습니다. 관리자에게 문의해 주세요."
            style={{ borderRadius: "16px" }}
          />
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "24px",
            maxWidth: "920px",
            margin: "0 auto",
            paddingBottom: "60px",
          }}
        >
          {chosen.map((p) => (
            <CustomerPersonaCard key={p.id} customerPersona={p} isPractice />
          ))}
        </div>
      </Container>
    </div>
  );
};

export default PracticePersonaSelect;
